import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { disposeChildren, disposeResizeObserver, setDisposable } from "@cervval.js/client-html5-core/src/components/disposable";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { Display, toggleClass, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { FLEX_CONTAINER, FLEX_CONTAINER_COLUMN, FLEX_ITEM_AUTO, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { AcceptedHtmlElementContent, createHtmlElement, empty, setHTMLElementContent } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import { getDuplicates, hasDuplicates } from "@cervval.js/common/src/tools/collection.array.tools";
import { getVOFValue, ValueOrFunction } from "@cervval.js/common/src/tools/object.tools";
import { valueOf } from "@cervval.js/common/src/tools/timestamps.tools.definitions";
import { NOW } from "@cervval.js/common/src/tools/timestamps.tools.factory";
import { Tab } from "../../bootstrap";
import { ButtonStyle, CjsButton } from "../button/button";
import { IconFA6 } from "../icon/tools/fontAwesome6IconList.tools";
import { i18n } from "./i18n/i18n";
export interface SwitchTabEventObject {

    /** Uid of tab */
    tabUid: string;

    /** Index of tab */
    tabIndex: number;
}

/** Event triggered with value of tab previously and currently displayed */
export type OnSwitchTabsEvent = {

    /** Tab who has been showed */
    newTab: SwitchTabEventObject,

    /** Tab who has been hidden */
    oldTab: SwitchTabEventObject

}

/** Event triggered when one of the nav buttons is clicked */
export type OnTabsMenuNavButtonClickEvent = {

    /** Button who has been clicked */
    navButton: "first" | "last"
}

/** Event triggered with value of picker has change */
export type OnPickerValueChangeEvent<ValueType> = {

    /** New value of picker */
    value: ValueType
}


/** Class to add when tab is active */
const ACTIVE_TAB_CLASS = "active";

export enum TabOrientation {

    /** Horizontal tabs links */
    HORIZONTAL = "horizontal",

    /** Vertical tabs links */
    VERTICAL = "vertical"
}

export interface TabContent {

    /** Component that will be displayed in the tab content */
    content: ValueOrFunction<AcceptedHtmlElementContent>;

    /** Refresh function for the component */
    refreshContent?: () => void;

    /** Timestamp used to trigger refresh (if data has changed since last refresh call) when tab become active */
    lastEventTime?: number;
}

export interface TabElement {

    /** Unique identifier of an item tab, used to know which tab is clicked */
    uid: ValueOrFunction<string>;

    /** Title content that will be displayed for this tab item */
    label: ValueOrFunction<AcceptedHtmlElementContent>;

    /** Menu item DOM element of the tab */
    menuItem?: HTMLLIElement;

    /** Tab content object */
    tabContent?: TabContent;

    /** Index of tab */
    tabIndex?: number;

    /** If true, tab is disabled */
    disabled?: ValueOrFunction<boolean>;
}

export interface TabsOptions {

    /**
     * Resize event handler debounce, value in ms
     */
    resizeEventDebounceValue: number;

    /**
     * Index of the current active tab
     * ! to change the active tab, you must use switchActiveTab method (changing the option is not enough)
     */
    activeTabIndex: number;

    /** Configurable event to listen to trigger refresh of active tab*/
    EVENT_TO_DISPATCH_KEY?: string;

    /** Orientation of tabs links menu (default: horizontal) */
    tabOrientation: TabOrientation;

    /** Enable mouse scroll behavior hover tabMenu */
    enableTabMenuScroll?: boolean;

    /**
     * Force your navMenu contents to extend the full available width one of two modifier classes.
     * To proportionately fill all available space with your navMenu, enable this option.
     * Notice that all horizontal space is occupied, but not every navMenu has the same width.
     */
    enableNavFill?: boolean;

    /**
     * For equal-width elements, enable this option. 
     * All horizontal space will be occupied by nav links,
     * but unlike the NavFill option, every nav item will be the same width.
     */
    enableNavJustified?: boolean;

    /**
     * Label for aria-label
     */
    arialabel?: string;
}

export const defaultTabsOptions: TabsOptions = {


    /**
     * Resize event handler debounce, value in ms
     */
    resizeEventDebounceValue: 20,


    /**
     * Index of the current active tab
     */
    activeTabIndex: 0,

    /** Orientation of tabs links menu (default: horizontal) */
    tabOrientation: TabOrientation.HORIZONTAL
};

export const TAB_INDEX_ATTRIBUTE = "tab-index";

/**
 * Tab component
 */
@CustomElement({
    name: "cjs-tabs",
    style: require("./tabs.scss"),
    template: require("./tabs.html"),
    i18n,
    elementCssClasses: [FLEX_ITEM_AUTO]
})
export class CjsTabs extends WebComponent {

    @Injected(LOG_SERVICE, { optional: true })
    protected readonly _log?: LogService;

    @TypedWebComponentOptions(defaultTabsOptions)
    public readonly options!: WebComponentOptions<TabsOptions>;

    @TypedHtmlElements({ ensureLoadedClasses: [CjsButton] })
    public readonly components!: {
        tabMenuContainer: HTMLDivElement,
        tabMenu: HTMLUListElement,
        tabContent: HTMLDivElement,
        firstButton: CjsButton,
        lastButton: CjsButton
    };

    /** Key of tab click event from tab component */
    @Event(NoBubblesEventInit)
    public switchTabEvent!: EventHandler<OnSwitchTabsEvent>;

    /** Event dispatch when tab content is shown */
    @Event(NoBubblesEventInit)
    public afterShowTabEvent!: EventHandler<TabElement>;

    /** Event dispatch when left or right tabMenu button is clicked */
    @Event(NoBubblesEventInit)
    public tabsMenuNavClickEvent!: EventHandler<OnTabsMenuNavButtonClickEvent>;

    /** Speed of wheel scrolling event, can be redefined to speed up or slow down scrolling speed */
    public wheelScrollEventSpeed = 3;

    /**
     * Array containing all tab item
     */
    private _tabs: TabElement[] = [];

    /**
     * Store array of tabContent "containers" (where the content of each tab will be added)
     */
    private _tabsContentContainers: HTMLDivElement[] = [];

    /**
     * Index of the first tab displayed in the dom, used when there is not enough space to render all tabs
     */
    private _currentIndexOfFirstTabDisplayed = 0;

    /**
     * Boolean to simulate a debounce on the resize function (to avoid several function calls per second)
     */
    private _canFireResizeEvent = true;

    /** Store last event received by Tabs component to refresh active tab content*/
    private _lastEvent?: CustomEvent;

    /** Store last _lastEvent timestamp to compare with new active tab timestamp and refresh content if needed*/
    private _lastEventTime?: number;

    /** WheelScroll event debounce counter, used to reduce scroll speed over tabMenuContainer */
    private _wheelScrollDebounceCounter = 0;

    /** elements to dispose when calling dispose function */
    protected _elementsToDispose: {
        resizeObserver?: ResizeObserver
    } = {};
    protected _isDisposed = false;

    constructor(options?: Partial<TabsOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        setDisposable(this);
        this.options.setWithoutRefresh(options);
    }

    /**
     * Cleanup
     */
    public dispose(p?: { doNotDisposeChildren?: boolean }): void {
        if (this._isDisposed) {
            return;
        }
        this._isDisposed = true;
        disposeResizeObserver(this._elementsToDispose);
        if (!p?.doNotDisposeChildren) { disposeChildren(this); }
        this.lightDomRoot = undefined;
    }

    /**
     * init component
     */
    public override init(): void {
        if (this._isDisposed) {
            // element was disposed => don't build it
            return;
        }

        this._initListeners();

        // Init buttons
        this.components.firstButton.options.setAndRefreshIfChanged({
            label: i18n().firstButtonLabel,
            cssStyle: ButtonStyle.SECONDARY,
            outline: true,
            iconMode: "only",
            sharp: true
        });

        this.components.lastButton.options.setAndRefreshIfChanged({
            label: i18n().lastButtonLabel,
            cssStyle: ButtonStyle.SECONDARY,
            outline: true,
            iconMode: "only",
            sharp: true
        });

        this._createTabs();
    }

    /**
     * Set component data
     * @param tabs List of tab to set data
     */
    public setData(tabs: TabElement[], refresh = true): void {
        this._tabs = tabs;
        if (refresh) {
            this._createTabs();
            if (this.isInitDone) {
                // if !isInitDone refresh will be called later
                this.refresh();
            }
        }
    }

    /**
     * Function that binds configurable event to listen to refresh new active tab content
     * @param eventKey 
     */
    public setEventToDispatch(eventKey: string): void {
        this.options.setWithoutRefresh({ EVENT_TO_DISPATCH_KEY: eventKey });

        if (eventKey && this.options.EVENT_TO_DISPATCH_KEY) {
            this.addEventListener(this.options.EVENT_TO_DISPATCH_KEY, (e) => {
                const event = e as CustomEvent;
                if (this._lastEvent !== event) {
                    this._lastEventTime = Date.now();
                    const activeTab = this._tabs[this.options.activeTabIndex];
                    if (activeTab?.tabContent) {
                        if (activeTab.tabContent.refreshContent) {
                            activeTab.tabContent.refreshContent();
                        }
                        activeTab.tabContent.lastEventTime = this._lastEventTime;
                    }
                    this._lastEvent = event;
                }
            });
        }
    }

    /**
     * Refresh listeners for buttons navigation mode
     */
    private _initListeners(): void {

        // Left button click
        this.components.firstButton.clickEvent.on(() => this._onFirstButtonClick(), { component: this, linkToDom: true, onlyIfVisible: true });

        // Right button click
        this.components.lastButton.clickEvent.on(() => this._onLastButtonClick(), { component: this, linkToDom: true, onlyIfVisible: true });

        // Add mouse wheel event listener if needed
        if (this.options.enableTabMenuScroll) {
            this.components.tabMenuContainer.addEventListener("wheel", e => this._handleWheelScrollEvent(e));
        }

        // When component resize
        const resizeObs = new ResizeObserver(entries => {
            for (const entry of entries) {
                if (entry.target === this.components.tabMenu) {
                    if (this._canFireResizeEvent) {
                        this._canFireResizeEvent = false;
                        setTimeout(() => {
                            this._handleResizeAndToggleButtonsDisplay(true);
                            this._canFireResizeEvent = true;
                        }, this.options.resizeEventDebounceValue);
                    }
                }
            }
        });
        resizeObs.observe(this.components.tabMenu);
        this._elementsToDispose.resizeObserver = resizeObs;
    }

    /**
     * Refresh function
     */
    public override refresh(): void {
        if (this._isDisposed) {
            this._log?.error("Element is disposed");
            return;
        }

        this._refreshTabOrientation();
        this._refreshMenuButtons();
        this.refreshTabs();
        this._handleResizeAndToggleButtonsDisplay(false);
    }

    /** Refresh tabs display */
    public refreshTabs(): void {

        // Check tabs uid duplicates in list of tabs, duplicates are forbidden
        this._checkTabsUidDuplicates();

        for (const tab of this._tabs) {
            if (tab.menuItem && tab.tabIndex !== undefined) {
                toggleVisible(tab.menuItem, this._currentIndexOfFirstTabDisplayed <= tab.tabIndex, Display.LIST_ITEM);
            }
            const link = this._getTabLinkElement(tab);
            if (link) {
                toggleClass(link, getVOFValue(tab.disabled), "disabled");
                setHTMLElementContent(link, getVOFValue(tab.label) ?? "");
            }

            // Refresh currently displayed tab
            if (tab.tabIndex != null && tab.tabIndex === this.getActiveTab()?.tabIndex) {
                tab.tabContent?.refreshContent?.();// Refresh content of tab
            }
        }

        // Refresh NavFill and NavJustified
        toggleClass(this.components.tabMenu, this.options.enableNavFill, "nav-fill");
        toggleClass(this.components.tabMenu, this.options.enableNavJustified, "nav-justified");
    }
    /**
     * Display the specific tabs
     * @param tabUid 
     */
    public switchActiveTabUid(tabUid: string): void {
        const tabToSelectIndex = this._tabs.findIndex((tab) => tab.uid === tabUid);
        if (tabToSelectIndex == null) {
            throw new Error(`Can't set active tab ${tabUid}, not found`);
        }

        this.switchActiveTab(tabToSelectIndex);
    }

    /**
     * Get tab by uid
     * @param uid of tab
     */
    public getTabByUid(uid: string): TabElement | undefined {
        return this._tabs.find(e => e.uid === uid);
    }

    /**
     * Get tab by index
     * @param index of tab
     */
    public getTabByIndex(index: number): TabElement | undefined {
        return this._tabs.find(e => e.tabIndex === index);
    }

    /** Get tab content container */
    public getTabContentContainerByUid(uid: string): HTMLElement | undefined {
        return this._tabsContentContainers.find(container => container.getAttribute("uid") === uid);
    }

    /** Refresh tabs menu orientation */
    private _refreshTabOrientation(): void {

        // Refresh orientation of Tab component (opposite display of menuContainer and tabMenu)
        toggleClass(this, !this._isHorizontalTab(), FLEX_CONTAINER);
        toggleClass(this, this._isHorizontalTab(), FLEX_CONTAINER_COLUMN);

        // Refresh orientation of menuContainer
        toggleClass(this.components.tabMenuContainer, this._isHorizontalTab(), FLEX_CONTAINER);
        toggleClass(this.components.tabMenuContainer, !this._isHorizontalTab(), FLEX_CONTAINER_COLUMN);

        // Refresh orientation of tabMenu
        toggleClass(this.components.tabMenu, this._isHorizontalTab(), FLEX_CONTAINER);
        toggleClass(this.components.tabMenu, !this._isHorizontalTab(), FLEX_CONTAINER_COLUMN);
    }


    /** Refresh buttons based on tab orientation */
    private _refreshMenuButtons(): void {

        // Refresh orientation of tabMenu
        toggleClass(this.components.firstButton, this._isHorizontalTab(), TabOrientation.HORIZONTAL);
        toggleClass(this.components.firstButton, !this._isHorizontalTab(), TabOrientation.VERTICAL);

        // Refresh orientation of tabMenu
        toggleClass(this.components.lastButton, this._isHorizontalTab(), TabOrientation.HORIZONTAL);
        toggleClass(this.components.lastButton, !this._isHorizontalTab(), TabOrientation.VERTICAL);

        this.components.firstButton.options.setAndRefreshIfChanged({
            icon: { name: `fa-${this._isHorizontalTab() ? IconFA6.CHEVRON_LEFT : IconFA6.CHEVRON_UP}` }
        });

        this.components.lastButton.options.setAndRefreshIfChanged({
            icon: { name: `fa-${this._isHorizontalTab() ? IconFA6.CHEVRON_RIGHT : IconFA6.CHEVRON_DOWN}` }
        });
    }

    /**
     * Handle click on left tab button
     */
    private _onFirstButtonClick(dispatchEvent = true): void {

        // Handle negative index
        if (this._currentIndexOfFirstTabDisplayed < 1) {
            return;
        }

        this._currentIndexOfFirstTabDisplayed -= 1;
        this.refreshTabs();

        // Handle disabled
        this.components.firstButton.options.setAndRefreshIfChanged({ disabled: this._currentIndexOfFirstTabDisplayed < 1 });
        this.components.lastButton.options.setAndRefreshIfChanged({ disabled: false });

        if (dispatchEvent) {
            // Dispatch event
            this.tabsMenuNavClickEvent.emit({ navButton: "first" });
        }
    }

    /**
     * Handle click on right tab button
     */
    private _onLastButtonClick(dispatchEvent = true): void {

        // Handle index over tabs length
        if (this._currentIndexOfFirstTabDisplayed >= this._tabs.length - 1 || !this._isOverflown(this.components.tabMenu)) {
            return;
        }

        // Update index displayed
        this._currentIndexOfFirstTabDisplayed += 1;
        this.refreshTabs();

        // Handle disabled
        this.components.lastButton.options.setAndRefreshIfChanged({ disabled: !this._isOverflown(this.components.tabMenu) });
        this.components.firstButton.options.setAndRefreshIfChanged({ disabled: false });

        if (dispatchEvent) {
            // Dispatch event
            this.tabsMenuNavClickEvent.emit({ navButton: "last" });
        }

    }

    /**
     * Create tab items and set active tab
     */
    private _createTabs(): void {

        // Clear tab menu and tab content
        empty(this.components.tabMenu);
        empty(this.components.tabContent);
        this._tabsContentContainers = [];
        this.components.tabMenu.ariaLabel = this.options.arialabel ?? "";

        // Append tab menu and tab content
        for (let i = this._currentIndexOfFirstTabDisplayed; i < this._tabs.length; i++) {
            const tab = this._tabs[i];

            // Create link
            const link = createHtmlElement("a", {
                cssClass: [W_100],
                innerHTML: getVOFValue(tab.label) ?? "",
                attributes: {
                    "data-bs-toggle": "tab",
                    "data-bs-target": `[uid='${tab.uid}']`,
                    "role": "tabpanel"
                }
            });

            // Add listner for after tab content show
            link.addEventListener("shown.bs.tab", _ => this.afterShowTabEvent.emit(tab));

            if (getVOFValue(tab.disabled)) {
                link.classList.add("disabled");
            }

            // Delete previous menuItem register
            if (tab.menuItem) {
                tab.menuItem.remove();
                delete tab.menuItem;
            }

            // Create list
            tab.menuItem = createHtmlElement("li", {
                cssClass: ["nav-item"],
                attributes: {
                    "data-tab-uid": getVOFValue(tab.uid) ?? "",
                    [TAB_INDEX_ATTRIBUTE]: String(i),
                    "tabindex": "0"
                }
            });
            tab.menuItem.append(link);
            // Give the priority at the tab name instead of saying "Tab"(Screen reader)
            tab.menuItem.setAttribute("role", "alert");
            tab.menuItem.addEventListener("click", () => this._onTabMenuClicked(tab, i));
            tab.menuItem.addEventListener("keydown", (event) => {
                if (event.key === "Enter") {
                    this._onTabMenuClicked(tab, i);
                }
            });

            tab.tabIndex = i;

            // Append tab to tab menu
            this.components.tabMenu.append(tab.menuItem);

            // Add div content associated for active table index
            this._createTabContent(i);

            // Set active tab for selected one
            if (i === this.options.activeTabIndex) {
                this._refreshActiveTabMenuDisplay(i);

                // Show index (with bootstrap plugin)
                if (link) {
                    const tabElement = new Tab(link);
                    tabElement.show();
                }
            }

        }

        if (this.options.activeTabIndex < this._currentIndexOfFirstTabDisplayed) {
            // Add div content associated for active table index
            this._createTabContent(this.options.activeTabIndex);
        }
    }

    /**
     * Set new active tab menu and refresh its content if needed
     * @param tabIndex index of the new active Tab
     */
    public switchActiveTab(tabIndex: number): void {

        // Handle out of range index
        if (tabIndex < 0 || tabIndex >= this._tabs.length) { return; }

        const tab = this._tabs[tabIndex];

        // Check if element exist and new index is different than current one
        if (!tab?.menuItem || tabIndex === this.options.activeTabIndex) { return; }

        const oldTab = this._tabs[this.options.activeTabIndex];

        const tabAElement = this._getTabLinkElement(tab);
        if (!tabAElement) { return; }

        // Set active tab index
        this.options.setWithoutRefresh({ activeTabIndex: tabIndex });

        // Show tab
        const tabElement = new Tab(tabAElement);
        tabElement.show();

        // Refresh selected menu display
        this._refreshActiveTabMenuDisplay(tabIndex);

        // Call a refresh on content of tabContent 
        if (tab?.tabContent?.content && tab.tabContent.refreshContent) {
            if (tab.tabContent.lastEventTime === undefined || this._lastEventTime === undefined || tab.tabContent.lastEventTime < this._lastEventTime) {
                tab.tabContent.refreshContent();
                tab.tabContent.lastEventTime = this._lastEventTime ?? valueOf(NOW());
            }
        }

        this.switchTabEvent.emit({
            newTab: {
                tabUid: getVOFValue(tab.uid) ?? "",
                tabIndex
            },
            oldTab: {
                tabUid: getVOFValue(oldTab.uid) ?? "",
                tabIndex: oldTab.tabIndex ?? -1
            }
        });

    }

    /**
     * @returns Active tab element
     */
    public getActiveTab(): TabElement | undefined {
        return this._tabs.find(tab => tab.tabIndex === this.options.activeTabIndex);
    }

    /** Callback called when a TabMenu is clicked, register by reference to removeListener */
    private _onTabMenuClicked = (tab: TabElement, tabIndex: number): void => {
        if (getVOFValue(tab.disabled)) { return; }
        this.switchActiveTab(tabIndex);
    };

    /**
     * Create div content associated to active tab index
     */
    private _createTabContent(tabIndex: number): void {

        // Get tab
        const tab = this._tabs[tabIndex];

        // Create content div
        const contentDiv = createHtmlElement("div", {
            cssClass: [
                "tab-pane",
                "fade",

                FLEX_CONTAINER_COLUMN,
                FLEX_ITEM_AUTO
            ],
            attributes: {
                // Set tab content uid used to display content when trigger click on tab
                "uid": getVOFValue(tab.uid) ?? ""
            },
            // Set tab content
            innerHTML: getVOFValue(tab.tabContent?.content) ?? ""
        });

        if (this.options.activeTabIndex === tabIndex) {
            contentDiv.classList.add(ACTIVE_TAB_CLASS, "show");
        }

        this._tabsContentContainers.push(contentDiv);
        this.components.tabContent.append(contentDiv);
    }

    /**
     * Handle display of left/right buttons
     * First case : Tab menu bigger than its containing div, we display buttons
     * Second case : Tab menu smaller than its containing div and all tabs are displayed, we hide buttons
     * Third case : Tab menu smaller than its containing div but not all tabs are displayed, we refresh tabs with one more tab displayed
     * @param updateTabs [optional] // If true we refresh tab menu, used in third case 
     */
    private _handleResizeAndToggleButtonsDisplay(updateTabs?: boolean): void {
        if (this._isOverflown(this.components.tabMenu)) {
            this.components.firstButton.style.display = this.components.lastButton.style.display = Display.BLOCK;
        } else if (this._currentIndexOfFirstTabDisplayed === 0) {
            this.components.firstButton.style.display = this.components.lastButton.style.display = Display.NONE;
        } else if (this._currentIndexOfFirstTabDisplayed > 0 && updateTabs) {
            this._currentIndexOfFirstTabDisplayed -= 1;
            this.refresh();
        }

        if (this._currentIndexOfFirstTabDisplayed < 1) {
            this.components.firstButton.options.setAndRefreshIfChanged({ disabled: true });
        }
    }

    /**
     * Set active class on tab menu item and remove active class from others menu item
     * @param tabIndex new active tab index
     */
    private _refreshActiveTabMenuDisplay(tabIndex: number): void {

        // Retrieve all menus
        const allMenus = this._tabs.filter(tab => Boolean(tab.menuItem)).map(tab => tab.menuItem!);

        // Add or remove active tab class for each tab
        allMenus.forEach(m => toggleClass(m, m.getAttribute(TAB_INDEX_ATTRIBUTE) === String(tabIndex), ACTIVE_TAB_CLASS));
    }

    /**
     * Behavior when mouse wheel scroll over 
     */
    private _handleWheelScrollEvent(e: WheelEvent): void {

        // Switch tab only all 
        if (this._wheelScrollDebounceCounter % this.wheelScrollEventSpeed === 0) {
            if (e.deltaY < 0) {// Backward behavior

                // Display previous tab if needed
                this._onFirstButtonClick(false);
            } else {// Forward behavior

                // Display next tab if needed
                this._onLastButtonClick(false);
            }
        }
        this._wheelScrollDebounceCounter++;
    }

    /** Retrieve the tabLink (<a>) element of a tab */
    private _getTabLinkElement(tab: TabElement): HTMLAnchorElement | undefined {
        return tab.menuItem?.firstChild as HTMLAnchorElement;// We know than li only contain <a> element
    }

    /**
     * Function that determines if DOM element is overflown horizontally by its content
     * Compare the element's width displayed (client width) with its total width (displayed + overflow = scroll width)
     * @param element HTMLElement
     * @returns boolean indicating if element is overflown or not
     */
    private _isOverflown(element: HTMLElement): boolean {
        return this._isHorizontalTab() ? element.scrollWidth > element.clientWidth + this.OVERFLOWN_CHECK_OFFSET : element.scrollHeight > element.clientHeight + this.OVERFLOWN_CHECK_OFFSET;
    }

    /** Offset linked to the scroll width compute, ensure scrollWidth higher than clientWidth */
    private readonly OVERFLOWN_CHECK_OFFSET = 1;// In px

    /** Check if the tab is in HORIZONTAL orientation */
    private _isHorizontalTab(): boolean {
        return this.options.tabOrientation === TabOrientation.HORIZONTAL;
    }

    /** Check tabs list uid duplicates */
    private _checkTabsUidDuplicates(): void {
        const tabUids = this._tabs.map(tab => tab.uid);
        if (hasDuplicates(tabUids)) {
            getLogConsole().warn(`[CjsTabs] - List of given tabs has uid duplicates": ${getDuplicates(tabUids).join(", ")}`);
        }
    }
}